/* eslint-disable max-lines-per-function */
import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import logger from "services/logger";
import { isPropertyFeatureEnabled } from "pages/dashboard/interests";
import { SlideoutApplications, SlideoutApplicationsVariables, Feature } from "types/gql-types";
import Tabs from "components/tabs";
import { query as applicationsQuery } from "./applications-page/queries";
import { links } from "./index";
type BuiltTab = {
  value: string;
  label: React.ReactNode;
  badgeContents?: React.ReactNode;
  badgeDataCy?: string;
  href?: string;
};

// Use shared Tabs component instead of custom styled list
type TabLink = { name: string; to: string; feature?: Feature };

const buildBaseFromPathUntilInterests = (pathname: string, id: string): string | null => {
  const idx = pathname.indexOf("/interests/");
  if (idx > -1) {
    const base = pathname.slice(0, idx).replace(/\/$/, "");
    return `${base}/interests/${id}`;
  }
  return null;
};

const getUpdateMiddle = (pathname: string, base: string): string => {
  if (pathname.includes(`${base}/contact`)) return "/contact";
  if (pathname.includes(`${base}/process/removals`)) return "/process/removals";
  return "";
};

const getBasePath = (
  interestId: string,
  params: { listId?: string; p?: string; interestId?: string },
  pathname?: string,
) => {
  const { listId, p: page, interestId: interestIdFromParams } = params;
  const id = interestIdFromParams || interestId;

  // If we're on the update route, build paths using the update prefix
  if (pathname && pathname.includes("/dash/manager/update")) {
    const base = `/dash/manager/update/${listId}`;
    const middle = getUpdateMiddle(pathname, base);
    return `${base}${middle}/interests/${id}`;
  }

  // If we're on the messages route, use messages prefix and derive from pathname
  if (pathname && pathname.includes("/dash/manager/messages")) {
    return (
      buildBaseFromPathUntilInterests(pathname, id) ||
      `/dash/manager/messages/interests/${id}`
    );
  }

  // If we're on the duplicates route, derive the base similarly
  if (pathname && pathname.includes("/dash/manager/duplicates")) {
    return (
      buildBaseFromPathUntilInterests(pathname, id) ||
      `/dash/manager/duplicates/${listId}/interests/${id}`
    );
  }

  // If page is known, we're on the list pagination route
  if (typeof page !== "undefined") {
    return `/dash/manager/lists/${listId}/p/${page}/interests/${id}`;
  }

  // Fallback: lists route without explicit page segment
  return `/dash/manager/lists/${listId}/interests/${id}`;
};

const extendTabLinks = (baseLinks: TabLink[], orgLists: unknown[]): TabLink[] => (
  (orgLists && (orgLists as unknown[]).length > 1) ? [...baseLinks, { name: "Applications", to: "applications" }] : [...baseLinks]
);

const buildVisibleTabLinks = (
  tabLinks: TabLink[],
  features: { name: Feature }[],
): TabLink[] => tabLinks.filter((link) => isPropertyFeatureEnabled(features, link.feature));

const getBadgeCount = (
  tabName: string,
  appCount: number,
  docCount: number,
): number | null => {
  if (tabName === "Applications") return appCount;
  if (tabName === "Documents") return docCount;
  return null;
};

const getBadgeDataCy = (tabName: string): string => {
  if (tabName === "Applications") return "app-count";
  if (tabName === "Documents") return "doc-count";
  return "default-count";
};

const buildTabs = (
  basePath: string,
  visibleTabs: TabLink[],
  appCount: number,
  docCount: number,
) => visibleTabs.map((link) => {
  const href = `${basePath}/${link.to}`;
  return {
    value: href,
    href,
    label: link.name,
    badgeContents: getBadgeCount(link.name, appCount, docCount),
    badgeDataCy: getBadgeDataCy(link.name),
  } as BuiltTab;
});

const getCurrentValue = (locationPath: string, basePath: string): string => (
  locationPath === basePath ? `${basePath}/activity` : locationPath
);

const Navigation: React.FC<{ interestId: string }> = ({ interestId }) => {
  const opts = { variables: { interestId } };
  const { data, error } = useQuery<SlideoutApplications, SlideoutApplicationsVariables>(
    applicationsQuery,
    opts,
  );
  const orgLists = data?.interest?.organization?.interestLists || [];
  const features = data?.interest?.interestList.property.features || [];
  const appCount: number = data?.otherInterests?.length || 0;
  const docCount: number = data?.interest?.documents?.length || 0;
  const baseLinks: TabLink[] = [...(links as TabLink[])];

  if (error) {
    logger.sendError(error);
    return <div>Error loading navigation</div>;
  }
  const tabLinks = extendTabLinks(baseLinks, orgLists);

  const params = useParams();
  const location = useLocation();
  const basePath = getBasePath(interestId, params, location.pathname);

  const featuresList = (features ?? []) as { name: Feature }[];
  const visibleTabs = buildVisibleTabLinks(tabLinks, featuresList);
  const builtTabs = buildTabs(basePath, visibleTabs, appCount, docCount);
  const currentValue = getCurrentValue(location.pathname, basePath);

  return (
    <Tabs value={currentValue} tabs={builtTabs} sx={{ border: "none" }} />
  );
};

export default Navigation;
